import { pick } from 'lodash';
import { defineStore } from 'pinia';

import { Config } from '@/config';
import { Constants } from '@/constants';
import { SavingError } from '@/errors';
import { changedPageFields, isHomepage, pagePath, type PageWritableFields } from '@/helpers/page.helper';
import { insertOrUpdate } from '@/helpers/store.helper';
import { i18n } from '@/plugins/i18n';
import apiService from '@/services/api.service';
import type { CommunityDto, PageDto, Pagination } from '@/services/types/api.types';
import { useCommunityStore } from './community.store';

export interface PagesState {
  pages: PageDto[];
  pageListItems: PageDto[];
  pagination: Pagination;
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

// The page read carries the page's blocks as well, which the block list reads from the page's own blocks route. The
// store keeps the page alone.
const PAGE_FIELDS = ['id', 'slug', 'title', 'status', 'theme_key', 'layout_key', 'language_enabled'] as const;

type PageWriteAction = 'create' | 'save' | 'delete' | 'setHomepage';

interface ApiRefusal {
  response?: { status?: number; data?: { code?: string; errors?: { param?: string }[] } };
}

// The refusals the API explains with a code, each the key of its message.
const refusalKeys: Record<string, string> = {
  'homepage-draft': 'homepage-draft',
  'page-slug-taken': 'slug-taken',
  'page-layout-not-offered': 'layout-not-offered',
};

/**
 * A refusal the API explains, as a message in the administrator's language worded for the action that failed, or
 * undefined for any other failure. A Joi refusal names the field, not the rule: a reserved slug and a malformed one
 * read the same, and the admin keeps no copy of the reserved list.
 */
function pageRefusal(error: unknown, action: PageWriteAction): SavingError | undefined {
  const response = (error as ApiRefusal)?.response;
  const code = response?.data?.code;
  if (code === 'page-is-homepage') {
    return new SavingError(
      i18n.global.t(action === 'delete' ? 'page.errors.homepage-delete' : 'page.errors.homepage-unpublish'),
    );
  }
  if (code && refusalKeys[code]) {
    return new SavingError(i18n.global.t(`page.errors.${refusalKeys[code]}`));
  }
  if (response?.status === 422) {
    const params = response.data?.errors?.map((error) => error.param) ?? [];
    if (params.includes('slug')) return new SavingError(i18n.global.t('page.errors.slug-invalid'));
    if (params.includes('theme_key')) return new SavingError(i18n.global.t('page.errors.theme-invalid'));
    if (params.includes('layout_key')) return new SavingError(i18n.global.t('page.errors.layout-not-offered'));
  }
  return undefined;
}

// Every write throws on failure, as `wrapSavingOperation` expects: the explained refusals as a `SavingError`.
const refused = (error: unknown, action: PageWriteAction) => pageRefusal(error, action) ?? error;

export const usePagesStore = defineStore({
  id: 'pages',
  state: (): PagesState => ({
    pages: [],
    pageListItems: [],
    pagination: {
      page: 1,
      rowsNumber: 0,
      rowsPerPage: Constants.Pagination.DefaultRowsPerPage,
    },
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): PageDto | undefined =>
        state.pages.find((page) => page.id === id),
    /**
     * The page on the site, as a draft when it is one, at an anchor when one is given: the homepage at the community's
     * root, any other page at its slug. None for a page that is not loaded, or that no address reaches.
     */
    previewURL:
      (state) =>
      (pageId: number, anchor: string = ''): string | undefined => {
        const communityStore = useCommunityStore();
        const community = communityStore.community;
        const page = state.pages.find((item) => item.id === pageId);
        if (!community || !page) return undefined;
        const path = pagePath(page, community.options?.pages?.homepage_id);
        if (path === null) return undefined;
        return `${communityStore.previewURL(community.id)}/${path}?preview=draft${anchor}`;
      },
  },
  actions: {
    async fetchAll(page: number = 1, rowsPerPage: number = Constants.Pagination.DefaultRowsPerPage) {
      this.isLoading = true;
      try {
        const response = await apiService.post<PageDto[]>(`${Config.api.v2}/pages/:search`, {
          page,
          pageSize: rowsPerPage,
        });
        if (response.success) {
          this.pageListItems = response.data ?? [];
          if (!response.pagination) return;
          this.pagination = {
            page: response.pagination.currentPage,
            rowsNumber: response.pagination.totalCount,
            rowsPerPage,
          };
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchById(pageId: number) {
      this.itemLoadingStates.add(pageId);
      try {
        // A draft page is read too: its blocks are edited before it is published.
        const { data } = await apiService.get<PageDto>(`${Config.api.v2}/pages/${pageId}`, { preview: 'draft' });
        insertOrUpdate(this.pages, pick(data, PAGE_FIELDS), 'id');
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(pageId);
      }
    },
    /** Creates the page, a draft, and returns it: whoever asked opens it. */
    async create(values: Pick<PageDto, 'title' | 'slug' | 'language_enabled'>): Promise<PageDto> {
      try {
        const { data } = await apiService.post<PageDto>(`${Config.api.v2}/pages`, values);
        insertOrUpdate(this.pages, pick(data, PAGE_FIELDS), 'id');
        return data;
      } catch (error) {
        throw refused(error, 'create');
      }
    },
    /** Sends the fields that differ from the saved page, and nothing when none does. */
    async save(saved: PageDto, values: PageWritableFields): Promise<boolean> {
      const changed = changedPageFields(saved, values);
      if (!Object.keys(changed).length) return true;
      try {
        const { data } = await apiService.put<PageDto>(`${Config.api.v2}/pages/${saved.id}`, changed);
        insertOrUpdate(this.pages, pick(data, PAGE_FIELDS), 'id');
        return true;
      } catch (error) {
        throw refused(error, 'save');
      }
    },
    /** Deletes the page, then reloads the list page given. */
    async delete(pageId: number, page: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/pages/${pageId}`);
      } catch (error) {
        throw refused(error, 'delete');
      }
      this.pages = this.pages.filter((item) => item.id !== pageId);
      await this.fetchAll(page, this.pagination.rowsPerPage);
      return true;
    },
    /**
     * The community's homepage, which a dialog names: the list's row when it is on the list page shown, else the page
     * already read, else read now. None when the community has no homepage, or it cannot be read.
     */
    async findHomepage(): Promise<PageDto | undefined> {
      const homepageId = useCommunityStore().community?.options?.pages?.homepage_id;
      if (homepageId === null || homepageId === undefined) return undefined;
      const listed = this.pageListItems.find((page) => isHomepage(page, homepageId));
      if (listed) return listed;
      if (!this.getItem(Number(homepageId))) await this.fetchById(Number(homepageId));
      return this.getItem(Number(homepageId));
    },
    /** Makes the page the homepage, and keeps the community's homepage as the API answers it. */
    async setHomepage(pageId: number): Promise<boolean> {
      try {
        const { data } = await apiService.post<CommunityDto['options']['pages']>(
          `${Config.api.v2}/community/:set-homepage`,
          { page_id: pageId },
        );
        const community = useCommunityStore().community;
        if (community) community.options.pages = data;
        return true;
      } catch (error) {
        throw refused(error, 'setHomepage');
      }
    },
  },
});
